import { useMemo } from 'react'
import { LATCH_LATEST } from '../constants'

/**
 * Centralizes the merge of `parameters` + snapshot `latch` + `timeseriesLatch`
 * picker state into the parameters object passed to `useBlueprintData`. Also
 * reports `dataReady`, which gates the main blueprint fetch in timeseries-mode
 * renderers to prevent a mount-time double-fetch (LATCH_LATEST -> real range).
 *
 * Behaviour notes
 * - Snapshot latch: when `isSelectableLatch && effectiveDate` is truthy, use
 *   the picked date; otherwise send the `LATCH_LATEST` sentinel for the
 *   backend to resolve to MAX(dateColumn).
 * - Timeseries latch (picker active): emit `(period, range)` from `tsSelection`.
 * - Timeseries latch (no picker mounted, or pre-pick on mount): emit the
 *   configured `defaultPeriod` ONLY. The range parameter is intentionally
 *   omitted so the backend's `defaultBehavior` (`latest` | `trailing` |
 *   `required`) decides what to do, rather than being shortcircuited by the
 *   `__latest` sentinel which always resolves to a single-period window.
 *
 * `latchParams` is a smaller object useful for hooks like
 * `useBlueprintLazyTreeData` that thread the latch scope into per-row child
 * fetches without polluting the broader `parameters` bag.
 *
 * @param {object} args
 * @param {object} args.parameters Caller-supplied parameters.
 * @param {object|null} args.latch Snapshot latch config (or null).
 * @param {object|null} args.timeseriesLatch Timeseries latch config (or null).
 * @param {boolean} args.isSelectableLatch True iff snapshot latch is in selectable mode.
 * @param {string|null} args.effectiveDate Picked snapshot date.
 * @param {boolean} args.isTimeseriesMode True iff renderer should show the timeseries picker.
 * @param {{period:string,range:object}|null} args.tsSelection Current picker selection.
 */
export default function useLatchedParameters ({
  parameters,
  latch,
  timeseriesLatch,
  isSelectableLatch = false,
  effectiveDate = null,
  isTimeseriesMode = false,
  tsSelection = null
}) {
  return useMemo(() => {
    let next = parameters
    let latchOnly = {}

    if (latch) {
      const snapshotValue = (isSelectableLatch && effectiveDate)
        ? effectiveDate
        : LATCH_LATEST
      next = { ...next, [latch.parameterName]: snapshotValue }
      latchOnly = { ...latchOnly, [latch.parameterName]: snapshotValue }
    }

    if (timeseriesLatch) {
      if (isTimeseriesMode && tsSelection) {
        next = {
          ...next,
          [timeseriesLatch.periodParameter]: tsSelection.period,
          [timeseriesLatch.rangeParameter]: tsSelection.range
        }
        latchOnly = {
          ...latchOnly,
          [timeseriesLatch.periodParameter]: tsSelection.period,
          [timeseriesLatch.rangeParameter]: tsSelection.range
        }
      } else {
        // Pre-pick (or no picker). Only emit period; OMIT the range so the
        // backend's `defaultBehavior` is what decides between latest /
        // trailing / required. Sending LATCH_LATEST here would shortcircuit
        // every purpose to single-period-latest regardless of configuration.
        next = {
          ...next,
          [timeseriesLatch.periodParameter]: timeseriesLatch.defaultPeriod
        }
        latchOnly = {
          ...latchOnly,
          [timeseriesLatch.periodParameter]: timeseriesLatch.defaultPeriod
        }
      }
    }

    // Gate fetching in timeseries-mode renderers: until the picker has emitted
    // a real selection, hold off so the renderer doesn't fire one fetch with
    // the default-path params and then a second with the picker selection.
    // Non-timeseries renderers are always ready.
    const dataReady = !isTimeseriesMode || tsSelection != null

    return {
      effectiveParameters: next,
      latchParams: latchOnly,
      dataReady
    }
  }, [parameters, latch, timeseriesLatch, isSelectableLatch, effectiveDate, isTimeseriesMode, tsSelection])
}
